import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, SliderSelectEvent } from '@theme/events';

/**
 * @typedef {Object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {Element} [lightboxSlider] - The `slider-component` inside the dialog (absent when mono-visual).
 *
 * @extends {DialogComponent}
 */
class ProductMediaLightbox extends DialogComponent {
  requiredRefs = ['dialog'];

  /** Index currently visible in the *main* gallery slider, kept in sync so the lightbox opens on the same slide. */
  #currentIndex = 0;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const { signal } = this.#abortController;

    // The main gallery's `slider-component` lives as a sibling inside this element (not inside the
    // dialog) and bubbles `slider:select`. The lightbox's own internal slider bubbles the same event
    // while browsing fullscreen — ignored here, it never needs to sync itself.
    this.addEventListener(ThemeEvents.sliderSelect, this.#handleSliderSelect, { signal });

    // The dialog opens with focus on the close button, which sits outside the lightbox slider, so the
    // slider's own arrow-key handling never fires. Relay the keystrokes to it from here.
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {Event} event */
  #handleSliderSelect = (event) => {
    if (!(event instanceof SliderSelectEvent)) return;
    if (event.target === this.refs.lightboxSlider) return;
    this.#currentIndex = event.detail.index;
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.defaultPrevented) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const { dialog, lightboxSlider } = this.refs;
    if (!dialog?.open || !lightboxSlider) return;

    // Already inside the slider: let its own handler deal with it.
    const target = event.target;
    if (target instanceof Node && lightboxSlider.contains(target)) return;
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName))
    ) {
      return;
    }

    const slider = /** @type {{ handlePrevClick?: () => void, handleNextClick?: () => void }} */ (
      /** @type {unknown} */ (lightboxSlider)
    );
    const scroll = event.key === 'ArrowLeft' ? slider.handlePrevClick : slider.handleNextClick;
    if (typeof scroll !== 'function') return;

    event.preventDefault();
    scroll.call(slider);
  };

  /**
   * Opens the lightbox, jumping to `index` when given (clicking a specific slide/image), otherwise
   * falling back to whichever slide is currently visible in the main gallery.
   *
   * @param {number} [index]
   */
  open(index) {
    this.showDialog();

    // The dialog is still closed at this point (`showDialog` defers `showModal`), so the lightbox
    // slider has no dimensions yet. `scrollToIndex` records the target and replays it once Embla
    // re-inits at full size — no need to guess how many frames that takes.
    const slider = this.refs.lightboxSlider;
    const jumpTo = /** @type {{ scrollToIndex?: (index: number) => void }} */ (/** @type {unknown} */ (slider))
      ?.scrollToIndex;
    if (typeof jumpTo === 'function') jumpTo.call(slider, typeof index === 'number' ? index : this.#currentIndex);
  }
}

if (!customElements.get('product-media-lightbox')) {
  customElements.define('product-media-lightbox', ProductMediaLightbox);
}
